import { SYNC_TRIGGER_LABELS } from '@acasa/shared'
import { EstateTable } from '@/features/checklists/estate-table'
import { exportChecklistCsv } from '@/features/checklists/export-csv'
import { Toolbar } from '@/features/checklists/toolbar'
import {
  useChecklists,
  useSyncChecklists,
  useSyncStatus,
} from '@/features/checklists/use-checklists'
import { useTableState, useVisibleEstates } from '@/features/checklists/use-table-state'
import { ApiRequestError } from '@/lib/api'
import { relativeTime } from '@/lib/relative-time'

function Metric({
  value,
  label,
  tone,
}: {
  value: number | string
  label: string
  tone?: 'done' | 'open'
}) {
  return (
    <div className="border-line border-l pl-3">
      <p
        className={`tnum font-mono text-lg leading-none ${
          tone === 'done' ? 'text-done' : tone === 'open' ? 'text-ink' : 'text-ink'
        }`}
      >
        {value}
      </p>
      <p className="text-faint mt-1 text-[10px] tracking-wider uppercase">{label}</p>
    </div>
  )
}

export function ChecklistsPage() {
  const { state, patch, toggleSort, resetFilters } = useTableState()
  const { data, isPending, isError, error } = useChecklists()
  const sync = useSyncChecklists()
  const status = useSyncStatus()
  const visible = useVisibleEstates(data, state)

  const visibleDocuments =
    data && state.visibleDocuments.length > 0
      ? data.documents.filter((document) => state.visibleDocuments.includes(document.key))
      : (data?.documents ?? [])

  /*
   * Busy if THIS tab started a run, or if the server says one is going — the
   * scheduled run, or a colleague's click. Either way a second start would only
   * be refused with a 409.
   */
  const running = sync.isPending || status.data?.running === true

  /*
   * A 409 is not a failure: somebody else's run is already doing exactly what
   * was asked. It reads as information, and the button already says why it is
   * disabled. Shown only while that run is still going (or the status is being
   * re-read after the refusal): once it ends, the note describes the past.
   */
  const alreadyRunning = sync.error instanceof ApiRequestError && sync.error.status === 409
  const showAlreadyRunning = alreadyRunning && (running || status.isFetching)

  /*
   * The status endpoint is the one that knows WHO started the last run; the
   * overview's `lastSync` predates the schedule and carries no trigger. Its
   * time is preferred for the same reason — both describe the same row.
   */
  const lastRun = status.data?.lastRun ?? null

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-wrap items-end justify-between gap-4 px-4 pt-6 pb-4">
        <div>
          <h1 className="font-display text-ink text-[22px] leading-none">Unterlagen</h1>
          <p className="text-muted mt-0.5 text-xs">
            Dokumenten-Checkliste je Objekt · lokaler Abzug aus onOffice
          </p>
        </div>

        <div className="flex items-end gap-4">
          {data && (
            <div className="flex gap-4">
              <Metric value={data.summary.estatesTracked} label="Objekte" />
              <Metric value={data.summary.openSteps} label="offene Unterlagen" />
              <Metric value={data.summary.estatesComplete} label="vollständig" tone="done" />
            </div>
          )}

          <div className="flex flex-col items-end gap-1">
            <button
              type="button"
              onClick={() => sync.mutate()}
              disabled={running}
              className="bg-brand text-surface hover:bg-brand-strong px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {running ? 'Abgleich läuft …' : 'Aus onOffice aktualisieren'}
            </button>
            {lastRun ? (
              <span
                className="text-faint text-[10px]"
                title={new Date(lastRun.finishedAt ?? lastRun.startedAt).toLocaleString('de-DE')}
              >
                {lastRun.status === 'running' ? 'gestartet' : 'Zuletzt'}{' '}
                {relativeTime(lastRun.finishedAt ?? lastRun.startedAt)} ·{' '}
                {SYNC_TRIGGER_LABELS[lastRun.trigger]}
                {lastRun.status === 'failed' && ' · fehlgeschlagen'}
              </span>
            ) : (
              data?.lastSync?.finishedAt && (
                <span className="text-faint text-[10px]">
                  {new Date(data.lastSync.finishedAt).toLocaleString('de-DE', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </span>
              )
            )}
          </div>
        </div>
      </header>

      {showAlreadyRunning && (
        <p className="border-line bg-raised text-muted mx-4 mb-3 border px-3 py-2 text-xs">
          {sync.error?.message} — die Liste wird danach von selbst aktualisiert.
        </p>
      )}

      {sync.isError && !alreadyRunning && (
        <p className="border-danger/30 bg-danger/5 text-danger mx-4 mb-3 border px-3 py-2 text-xs">
          Abgleich fehlgeschlagen:{' '}
          {sync.error instanceof Error ? sync.error.message : 'Unbekannter Fehler'}
          {sync.error instanceof ApiRequestError && sync.error.status === 503 && (
            <span className="text-muted mt-1 block">
              Sind ONOFFICE_API_TOKEN und ONOFFICE_API_SECRET in der .env gesetzt?
            </span>
          )}
        </p>
      )}

      {isPending && <p className="text-muted px-4 py-16 text-center text-sm">Wird geladen …</p>}

      {isError && (
        <p className="border-danger/30 bg-danger/5 text-danger mx-4 border px-3 py-2 text-xs">
          {error instanceof Error ? error.message : 'Unbekannter Fehler'}
        </p>
      )}

      {data && (
        <div className="border-line bg-surface mx-4 mb-4 flex min-h-0 flex-1 flex-col border">
          <Toolbar
            data={data}
            state={state}
            patch={patch}
            resetFilters={resetFilters}
            visibleCount={visible.length}
            onExport={() => exportChecklistCsv(visible, visibleDocuments)}
          />

          <EstateTable
            estates={visible}
            documents={data.documents}
            state={state}
            onSort={toggleSort}
          />

          <div className="border-line text-faint flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 border-t px-4 py-2.5 text-[11px]">
            <span className="flex items-center gap-1.5">
              <span className="bg-done size-3 rounded-[2px]" /> vorhanden
            </span>
            <span className="flex items-center gap-1.5">
              <span className="bg-open size-3 rounded-[2px]" /> offen
            </span>
            <span className="flex items-center gap-1.5">
              <span className="hatch border-na size-3 rounded-[2px] border" /> nicht erforderlich
            </span>
            <span className="flex items-center gap-1.5">
              <span className="bg-open ring-brand/50 size-3 rounded-[2px] ring-1" /> mit Notiz
            </span>
            <span className="text-faint">
              Vollständigkeit zählt „nicht erforderlich“ als erledigt
            </span>
            <span className="ml-auto">Zeile anklicken für Details</span>
          </div>
        </div>
      )}
    </div>
  )
}
